import log from "loglevel";
import { v4 as uuid } from "uuid";
import { Measures } from "../../../telemetry/Measures";
import { Trace } from "../../../telemetry/Trace";
import { dashboard } from "../../../telemetry/TraceLogging";
import { getWidgetOrchestrator } from "../orchestrator/orchestrator";
/*
 *
 * This class is a pattern for global widget observation
 *  when a specific global event needs to trigger logic exactly once.
 *
 * Example: when 1/2 of all widgets enter their "error" state, do something
 *  like redirect to a global error page.
 *
 * Expected callflow:
 *  - constructor() once per unique observer, to be called before page setup,
 *     then create a singleton getter like 'getLoadingWidgetLifecycleObserver()'
 *  - setNumWidgets() must be called by the InstanceManager during its initialization
 *     to ensure ordering.
 *  - registerTrigger() to define and create the unique conditions for the trigger
 *  - unregisterTrigger() shut down the observer if some external condition applies
 *  - callbackWrapper() tracks relevant state changes by all widgets. Triggers and
 *     shuts down the observer when the trigger conditions from registerTrigger()
 *     are met.
 *
 */
export class WidgetLifecycleObserver {
    constructor() {
        this._activated = false;
        this._callback = null;
        this._callbackWrapperWithContext = () => { };
        this._counterSet = new Set();
        this._id = uuid();
        this._stateCurrent = "unknown";
        this._threshold = -1;
        this._thresholdDivisor = -1;
        this._widgets = new Array();
        log.debug("wlo - [ctor] id:", this._id);
        Trace.logEvent(dashboard, "WebAppLifecycleObserverCreated", 0 /* verbose */, {
            id: this._id,
        });
    }
    setNumWidgets(numWidgets) {
        log.debug("wlo - [setNumWidgets] id:", this._id);
        if (this._thresholdDivisor === -1) {
            log.error("wlo - [setNumWidgets] id:", this._id, " thresholdDivisor uninitialized (registerTrigger probably needs to be called)");
        }
        else if (numWidgets <= 0) {
            log.info("wlo - [setNumWidgets] id:", this._id, "invalid divisor value");
            this._activated = false; // Will need to re-register. Setting to an invalid value turns the Observer off
        }
        else {
            this._threshold = Math.round(numWidgets / this._thresholdDivisor);
            log.info("wlo - [setNumWidgets] id:", this._id, " => ", this._threshold);
        }
        Trace.logEvent(dashboard, "WebAppLifecycleObserverWidgetCountSet", 0 /* verbose */, {
            id: this._id,
            count: numWidgets,
            threshold: this._threshold,
        });
    }
    registerTrigger(triggerThresholdDivisor, currentState, callback) {
        log.debug("wlo - [registerTrigger] id:", this._id);
        // Only one widget's registration allowed per WLO instance
        if (!this._activated && !this._callback) {
            this._activated = true;
            this._callback = callback;
            this._callbackWrapperWithContext = this.callbackWrapper.bind(this);
            this._stateCurrent = currentState;
            this._thresholdDivisor = triggerThresholdDivisor;
        }
        Trace.logEvent(dashboard, "WebAppLifecycleObserverTriggerRegistered", 0 /* verbose */, {
            id: this._id,
            currentState: this._stateCurrent,
            thresholdDivisor: this._thresholdDivisor,
        });
    }
    registerWidget(widget) {
        log.debug("wlo - [registerWidget] id:", this._id, widget.widgetId);
        this._widgets.push(widget);
        // Register for state change notifications and update the observer with the current state
        // of the widget.
        widget.addStateChangeListener(this._callbackWrapperWithContext);
        this.updateWidgetState(widget, widget.widgetState);
        Trace.logEvent(dashboard, "WebAppLifecycleObserverWidgetRegistered", 0 /* verbose */, {
            appId: widget.widgetRegistration.appId,
            id: this._id,
            indexedDBInstanceId: widget.instanceId,
            providerId: widget.widgetRegistration.providerId,
            widgetDefinitionId: widget.widgetRegistration.id,
            widgetInstanceId: widget.widgetId,
        });
    }
    unregisterTrigger() {
        log.debug("wlo - [unregisterTrigger] id:", this._id);
        if (this._callbackWrapperWithContext) {
            this._widgets.forEach((widget) => {
                widget.removeStateChangeListener(this._callbackWrapperWithContext);
            });
            this._activated = false;
        }
        Trace.logEvent(dashboard, "WebAppLifecycleObserverTriggerUnregistered", 0 /* verbose */, {
            id: this._id,
        });
    }
    callbackWrapper(widgetInstance, previousState, currentState) {
        log.debug("wlo - [callbackWrapper] id:", this._id, "==", previousState, "=>", currentState, this._threshold);
        this.updateWidgetState(widgetInstance, currentState);
    }
    updateWidgetState(widgetInstance, currentState) {
        if (this._activated && this._stateCurrent === currentState) {
            this._counterSet.add(widgetInstance.widgetId);
            if (this._threshold !== -1 && this._counterSet.size >= this._threshold && this._callback) {
                Trace.logEvent(dashboard, "WidgetLifecycleObserverUpdateWidgetState", 0 /* verbose */, {
                    id: this._id,
                    widget: this._widgets,
                });
                log.info("wlo - [handleWidgetStateChange] id:", this._id, " - called", this._widgets);
                this._callback(Array.from(this._counterSet.keys()), this._threshold, this._widgets.length);
                this.unregisterTrigger();
            }
        }
    }
}
// Singleton instance
let g_widgetLifecycleObserverLoadingInstance;
export function getLoadingWidgetLifecycleObserver() {
    if (!g_widgetLifecycleObserverLoadingInstance) {
        g_widgetLifecycleObserverLoadingInstance = new WidgetLifecycleObserver();
        g_widgetLifecycleObserverLoadingInstance.registerTrigger(1 /* triggerThresholdDivisor == numWidgets */, "loading", () => {
            Trace.logEvent(dashboard, "WLOWidgetInstancesCreatedForPeregrineContent", 0 /* verbose */);
            log.info("All widget instances created and ready for Peregrine content");
            Trace.logEvent(dashboard, "WebAppPeregrineLoadWaiterTriggered", 0 /* verbose */);
            getWidgetOrchestrator().then((orchestrator) => {
                orchestrator.listenForPeregrineStatus();
                orchestrator.loadPeregrineWidgets();
            });
        });
    }
    return g_widgetLifecycleObserverLoadingInstance;
}
// Singleton instance
let g_widgetLifecycleObserverErrorInstance;
export function getErrorWidgetLifecycleObserver() {
    if (!g_widgetLifecycleObserverErrorInstance) {
        g_widgetLifecycleObserverErrorInstance = new WidgetLifecycleObserver();
        g_widgetLifecycleObserverErrorInstance.registerTrigger(2 /* triggerThresholdDivisor == 1/2 widgets */, "error", (widgetIds, numThreshold, numWidgets) => {
            Measures.logEvent(dashboard, "WidgetsGlobalError", 16777216 /* productAndServicePerformance */, {
                failedWidgets: JSON.stringify(widgetIds),
                numFailedWidgets: widgetIds.length,
                numTotalWidgets: numWidgets,
                threshold: numThreshold,
            });
            log.error("Too many widgets have failed - sending to global error page = [", widgetIds, "] ", numThreshold, "/", numWidgets);
            // TODO: Send to global error state page? https://task.ms/32209387
        });
    }
    return g_widgetLifecycleObserverErrorInstance;
}
